import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { LocalStorageService } from "ngx-store";
import { of } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { Page, PageNumber } from "../model/pagination";
import { RunPlanActivity } from "../model/run";
import { Url } from "../model/users.model";
import { RunTaskFilterConfig, toFilterParams } from "../pages/dispatch/run/run-tasks/run-task-filter/run-task-filter";

/**
 * Service for handling all sorts of CRUD operations on tasks.
 *
 * By run tasks, we mean records from the `run_plan_activities` table.
 * On the other hand, run events are referred cannonically.
 */
@Injectable({
  providedIn: 'root'
})
export class RunTasksService {
  /**
   * Default fetch options when requesting for planned task resources.
   */
  public readonly fetchOptions: FetchOptions = {
    per_page: -1,
    include : [
      'run',
      'location.state',
      'runAction',
      'connote.customer',
      'connote.chargingMethod',
      'connoteSite',
      'connoteSite.location.state',
      'actual.location.state',
      'creator',
      'geofence.creator'
    ]
  };

  public constructor(
    private client : HttpClient,
    private storage: LocalStorageService
  ) { }

  /**
   * Fetch task resource collection from the server.
   * @param options Customize how to fetch the resource collection.
   * @returns Paginated task collection.
   */
  public fetchTasks(options: FetchOptions) {
    const params = toHttpParams(options);
    return this.client.get<Page<RunPlanActivity>>(this.url, { params });
  }

  /**
   * Get the task details from cache. If not in cache, first
   * retrieve the task details from server and store it in cache.
   * @param task Task details to retrieve.
   * @param options Dictate how to fetch the task details.
   * @returns Observable of the retrieved task.
   */
  public getTask(task: Pick<RunPlanActivity, 'id'>, options: Pick<Partial<FetchOptions>, 'include'> = this.fetchOptions) {
    const cachedTask = this.tasksCache.find((cached) => cached.id === task.id);

    return cachedTask
      ? of(cachedTask)
      : this.fetchTask(task, options)
  }

  /**
   * Get the task information from server, store it in cache, and propagate
   * updates to the task-list cache.
   * @param task Task to query.
   * @param options Query options.
   * @returns Task information retrieved, or `null` on failure.
   */
  public fetchTask(task: Pick<RunPlanActivity, 'id'>, options: Pick<Partial<FetchOptions>, 'include'> = this.fetchOptions) {
    const params = toHttpParams(options);
    return this.client
      .get<RunPlanActivity>(`${this.url}/${task.id}`, { params })
      .pipe(
        // update the task list cache
        tap((fetchedTask) => {
          if(fetchedTask) {
            const toUpdateIndex  = this.tasksCache.findIndex((cache) => fetchedTask.id === cache.id);

            toUpdateIndex !== -1
              ? this.tasksCache.splice(toUpdateIndex, 1, fetchedTask)
              : this.tasksCache.push(fetchedTask);
          }
        }),
        catchError(() => {
          console.error(`Planned task resource ${task.id} cannot be fetched.`);
          return of<null>(null);
        })
      );
  }

  /**
   * Update a planned task on the server.
   * @param task Task whose values to save.
   * @param options Specify how to fetch the saved task resource.
   * @returns Observable of the saved task from server, or `null` on failure.
   */
  public saveTask(task: RunPlanActivity, options: Pick<FetchOptions, 'include'> = this.fetchOptions) {
    const params = toHttpParams(options);
    const requestSave$ = task?.id
      ? this.client.put<RunPlanActivity>(`${this.url}/${task.id}`, task, { params })
      : this.client.post<RunPlanActivity>(this.url, task, { params });

    return requestSave$
      .pipe(
        tap((updatedTask) => {
          const updatedCache       = [...this.tasksCache];
          const cacheIndexToUpdate = updatedCache.findIndex((cached) => cached.id === updatedTask.id);

          cacheIndexToUpdate !== -1
            ? updatedCache.splice(cacheIndexToUpdate, 1, updatedTask)
            : updatedCache.push(updatedTask);

          this.tasksCache = updatedCache;
        }),
        catchError(() => {
          console.error(`Updates to planned task ${task.id} cannot be saved.`);
          return of<null>(null);
        })
      );
  }

  /**
   * Server API endpoint for run tasks resources.
   */
  private url = `${(this.storage.get('url') as Url).rest}/tms/run-plan-activities`;

  /**
   * Cache of the list of all tasks.
   */
  private tasksCache: RunPlanActivity[] = [];
}

/**
 * HTTP request parameters for fetching the task collections.
 */
interface FetchOptions {
  /**
   * How many resources to fetch per page.
   */
  per_page?: number;

  /**
   * Related resources included when fetching
   * run task resources.
   */
  include?: ResourceRelationship[];

  /**
   * Page offset.
   */
  page?: PageNumber;

  filters?: RunTaskFilterConfig
}

/**
 * List of related resources of the run task resource.
 */
type ResourceRelationship = 'run'
  | 'connote'
  | 'connote.customer'
  | 'connote.chargingMethod'
  | 'connoteSite'
  | 'connoteSite.actual'
  | 'connoteSite.location'
  | 'connoteSite.location.state'
  | 'location'
  | 'location.state'
  | 'runAction'
  | 'geofenceEvents'
  | 'geofence'
  | 'geofence.creator'
  | 'actual'
  | 'actual.location'
  | 'actual.location.state'
  | 'creator';

/**
 * Convert available fetch options provided by the API into {@link HttpParams}.
 * @param options Fetch options.
 * @returns Instance of {@link HttpParams}.
 */
function toHttpParams(options: Partial<FetchOptions>) {
  const fromObject = {
    per_page: options.per_page?.toString(),
    page    : options.page?.toString(),
    include : options.include?.join(',')
  };

  toFilterParams(options?.filters).forEach((value, key) => {
    fromObject[key] = value;
  });

  Object.entries(fromObject)
    .filter(([_, value]) => value === undefined)
    .forEach(([key]) => delete fromObject[key]);

  return new HttpParams({ fromObject });
}
